import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, HeartPulse, Check, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { fmtDataLunga } from "../pages/Infettive";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { EVENTO_LABEL, eventoColor } from "../lib/eventiSanitari";
import { VisiteDaTogliere, togliVisiteEvento } from "./VisiteEvento";
import { SAN } from "../lib/cfgSanitarie";


const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const INF_LABEL = EVENTO_LABEL;

export const PriorityBoxes = ({ wrapperClass = "grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6 items-start", showEventi = true, showCritici = true, eventiCols = 1 }) => {
  const { selectedStructure, structures = [] } = useAuth();
  const { signerName } = useFirma();
  const nav = useNavigate();
  const qc = useQueryClient();
  const params = { structure_id: selectedStructure };
  const todayIso = ymd(new Date());
  const allStruct = selectedStructure === "all";
  const structOf = (id) => structures.find((s) => s.id === id) || {};

  const { data: infOggi = [] } = useQuery({ queryKey: ["infettive-oggi", selectedStructure], queryFn: async () => (await api.get("/dashboard/infettive-oggi", { params: { ...params, giorni: 3 } })).data, refetchInterval: 30000 });
  const { data: critici = { items: [], ospiti: [], totale: 0 } } = useQuery({ queryKey: ["parametri-critici", selectedStructure], queryFn: async () => (await api.get("/dashboard/parametri-critici", { params: { ...params, giorni: 3 } })).data, refetchInterval: 30000 });

  const [febbreRisolvi, setFebbreRisolvi] = useState(null);
  const [febbreBusy, setFebbreBusy] = useState(false);
  const confermaFebbreRisolta = async () => {
    setFebbreBusy(true);
    try {
      if (!(await togliVisiteEvento(febbreRisolvi.ospite_id, febbreRisolvi.scelte, signerName))) return;
      await api.post("/infettive/febbre-risolta", { ospite_id: febbreRisolvi.ospite_id, structure_id: febbreRisolvi.structure_id, note: febbreRisolvi.note || "", operatore: signerName || undefined });
      toast.success(`Febbre segnata come risolta — ${febbreRisolvi.nome}`);
      qc.invalidateQueries({ queryKey: ["infettive-oggi"] });
      qc.invalidateQueries({ queryKey: ["nav-badges"] });
      setFebbreRisolvi(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setFebbreBusy(false); }
  };

  return (
    <>
      <div className={wrapperClass} data-testid={showEventi ? "priority-boxes-row" : "priority-boxes-critici"}>
        {showEventi && <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-4 shadow-sm" data-testid="infettive-oggi-card">
          <div className="flex items-center gap-2 mb-3 font-head font-bold text-red-800">
            <ShieldAlert size={18} /> Eventi sanitari in corso
            <button onClick={() => nav("/infettive")} className="ml-auto text-xs font-semibold text-primary shrink-0" data-testid="dash-infettive-link">Apri →</button>
            <span className="text-white text-xs font-bold rounded-full px-2.5 py-0.5 tabnum bg-red-600" data-testid="infettive-oggi-count">{infOggi.length}</span>
          </div>
          {infOggi.length === 0 ? (
            <div className="text-sm text-red-800 font-semibold">Nessun evento sanitario in corso.</div>
          ) : (
            <div className={`${eventiCols === 2 ? "grid grid-cols-1 lg:grid-cols-2 gap-2 items-start" : "space-y-2"} ${infOggi.length > (eventiCols === 2 ? 12 : 6) ? "max-h-[30rem] overflow-y-auto pr-1" : ""}`} data-testid="infettive-oggi-list">
              {infOggi.map((i) => {
                const nome = i.tipo === "altro" && (i.tipo_altro || "").trim() ? i.tipo_altro.trim() : (INF_LABEL[i.tipo] || (i.febbre ? "Febbre" : "Evento sanitario"));
                const gg = i.da ? Math.max(1, Math.round((new Date(todayIso + "T00:00:00") - new Date(i.da + "T00:00:00")) / 86400000) + 1) : null;
                const prolungato = gg != null && gg >= SAN.giorni_prolungato;
                return (
                <div key={i.ospite_id} className="flex items-center gap-2 rounded-xl border px-3 py-2 bg-white" data-testid={`infettive-oggi-${i.ospite_id}`}>
                  <button onClick={() => nav("/infettive")} className="flex-1 min-w-0 text-left hover:opacity-70 transition-opacity">
                    <div>
                      <span className="font-semibold">{i.nome}</span>{allStruct ? <span className="text-xs text-muted-foreground font-normal"> · {structOf(i.structure_id).nome || ""}</span> : ""}
                      <span className="ml-2 font-bold" style={{ color: eventoColor(i.tipo).txt }}>{nome}{i.temperatura ? ` · ${i.temperatura}°` : ""}</span>
                      {i.isolamento && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white align-middle">Isolamento</span>}
                      {prolungato && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white align-middle">prolungato</span>}
                    </div>
                    {i.da && <div className="text-xs text-slate-600 mt-0.5">Inizio {fmtDataLunga(i.da)}</div>}
                    {i.medico && <div className="text-xs text-slate-700 mt-0.5"><Stethoscope size={11} className="inline mr-1 text-slate-500" />{i.medico}{i.medico_tel && <span className="text-[11px] text-slate-500"> · {i.medico_tel}</span>}</div>}
                    {gg != null && <div className={`text-xs font-bold ${prolungato ? "text-red-700" : "text-amber-700"}`}>{gg} {gg === 1 ? "giorno" : "giorni"} dall'inizio</div>}
                  </button>
                  {i.febbre && (
                    <div className="shrink-0 flex flex-col items-end gap-0.5">
                      <button onClick={() => setFebbreRisolvi({ ospite_id: i.ospite_id, structure_id: i.structure_id, nome: i.nome, note: "" })} title="Risolvi evento sanitario"
                        className="text-xs font-semibold inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 border border-emerald-200 rounded-lg px-2 py-1 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                        data-testid={`febbre-risolvi-${i.ospite_id}`}><Check size={13} /> Risolvi</button>
                    </div>
                  )}
                </div>
              ); })}
            </div>
          )}
        </div>}

        {showCritici && <div className="gr-card p-5" data-testid="parametri-critici-card">
          <div className="flex items-center justify-between mb-4"><h2 className="font-head font-bold flex items-center gap-2"><HeartPulse size={18} className="text-rose-600" /> Parametri critici (ultimi 3 giorni)</h2><button onClick={() => nav("/parametri")} className="text-xs font-semibold text-primary" data-testid="dash-parametri-link">Apri →</button></div>
          {(!critici.ospiti || critici.ospiti.length === 0)
            ? <div className="text-sm text-muted-foreground">Nessun parametro critico negli ultimi 3 giorni</div>
            : <div className="grid grid-cols-1 gap-2">
                {critici.ospiti.map((o) => (
                  <button key={o.ospite_id} onClick={() => nav(`/parametri`)} data-testid={`parametri-critici-${o.ospite_id}`}
                    className="w-full flex flex-col items-start gap-0.5 text-sm rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 px-3 py-2 text-left transition-colors">
                    <span className="font-semibold">{o.nome}</span>
                    <span className="text-xs text-red-700 font-semibold">{(o.params_detail && o.params_detail.length ? o.params_detail : o.params).join(", ")}</span>
                  </button>
                ))}
              </div>}
        </div>}
      </div>

      <Dialog open={!!febbreRisolvi} onOpenChange={(o) => !o && setFebbreRisolvi(null)}>
        <DialogContent data-testid="febbre-risolvi-dialog">
          <DialogHeader>
            <DialogTitle>Febbre risolta — {febbreRisolvi?.nome}</DialogTitle>
            <DialogDescription>Conferma la risoluzione della febbre. L'ospite non comparirà più tra le allerte finché non verrà registrata una nuova febbre.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-semibold mb-1 block">Note (facoltative)</label>
            <Textarea rows={3} value={febbreRisolvi?.note || ""} onChange={(e) => setFebbreRisolvi((p) => ({ ...p, note: e.target.value }))} placeholder="Es. sfebbrato da 24h, medico avvisato..." data-testid="febbre-risolvi-note" />
          </div>
          <VisiteDaTogliere ospiteId={febbreRisolvi?.ospite_id} scelte={febbreRisolvi?.scelte || {}} setScelta={(id, s) => setFebbreRisolvi((p) => ({ ...p, scelte: { ...(p.scelte || {}), [id]: s } }))} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFebbreRisolvi(null)}>Annulla</Button>
            <Button onClick={confermaFebbreRisolta} disabled={febbreBusy} data-testid="febbre-risolvi-conferma"><Check size={15} className="mr-1" /> Conferma risolta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
